import useEmblaCarousel from "embla-carousel-react";
import { KeyboardEvent, useCallback, useEffect, useState } from "react";
import { useEventCallback } from "usehooks-ts";

interface CarouselOptions {
  loop?: boolean;
}

export const useCarousel = (options: CarouselOptions = {}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: "auto",
    align: "start",
    ...options,
  });

  const [hasPrev, setHasPrev] = useState(false);
  const [hasNext, setHasNext] = useState(false);

  const updateButtons = useEventCallback(() => {
    setHasPrev(emblaApi?.canScrollPrev() ?? false);
    setHasNext(emblaApi?.canScrollNext() ?? false);
  });

  const scrollPrev = useEventCallback(() => {
    emblaApi?.scrollPrev();
  });

  const scrollNext = useEventCallback(() => {
    emblaApi?.scrollNext();
  });

  const scrollToFirst = useEventCallback(() => {
    emblaApi?.scrollTo(0);
  });

  const scrollToLast = useEventCallback(() => {
    const snapList = emblaApi?.scrollSnapList();
    if (snapList) {
      emblaApi?.scrollTo(snapList.length - 1);
    }
  });

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          scrollPrev();
          break;
        case "ArrowRight":
          event.preventDefault();
          scrollNext();
          break;
        case "Home":
          event.preventDefault();
          scrollToFirst();
          break;
        case "End":
          event.preventDefault();
          scrollToLast();
          break;
        default:
          break;
      }
    },
    [scrollPrev, scrollNext, scrollToFirst, scrollToLast],
  );

  useEffect(() => {
    if (!emblaApi) return;

    updateButtons();
    emblaApi.on("reInit", updateButtons).on("select", updateButtons);

    return () => {
      emblaApi.off("reInit", updateButtons).off("select", updateButtons);
    };
  }, [emblaApi, updateButtons]);

  return {
    hasPrev,
    hasNext,
    carouselApi: emblaApi,
    carouselRef: emblaRef,
    scrollPrev,
    scrollNext,
    scrollToFirst,
    scrollToLast,
    handleKeyDown,
  };
};
